<template>
  <b-container
    v-if="sensor"
    id="print-sensor-data"
    class="sensor-data"
    fluid
    :key="sensor.id"
  >
    <b-row>
      <engineering-modules-header
        show-update-icon
        :show-info-icon="showInfoIcon"
        show-print-icon
        show-treeview-icon
        :show-wifi-icon="this.$hasUserPermission('PER010')"
        :company="installation.companyName"
        :installation="installation.name"
        :regional="installation.regionalName"
        :asset="asset.name"
        :title="sensor.name"
        :image-url="sensor.imageUrl"
        :notifications="notifications"
      />
    </b-row>
    <b-row class="mt-3 px-3">
      <b-tabs class="w-100" v-model="activeTab">
        <b-tab
          :key="tab.name"
          :title="tab.title"
          :class="tab.class"
          :active="isTabActive(tab)"
          @click="changeRouteTab(tab)"
          v-for="(tab, index) in tabs"
        >
          <s-loader v-if="loading" class="p-5" />
          <component
            v-if="renderTab(index)"
            class="w-100"
            :is="tab.component"
            :sensor="sensor"
            :asset="asset"
            :category="sensor.category"
            :selected-information="selectedInformation"
            :sensor-information="sensorInformation"
            :is-sensor-componentized="isSensorComponentized"
            @on-change-selected-information="onChangeSelectedInformationHandler"
          />
        </b-tab>
      </b-tabs>
    </b-row>
    <digital-network-modal
      v-model="showModal"
      :asset-id="assetId"
      :company="installation.companyName"
      :installation="installation.name"
      :regional="installation.regionalName"
      :asset="asset.name"
    />
  </b-container>
  <s-empty-list class="w-100" v-else />
</template>
<script>
import eventBus from '@/services/globalEventBus';
import ModuleHeader from '@/domain/components/installation/Header';
import featureTabs from '@/domain/data/installation/supervision/sensors/featureTabs';
import ResourceService from '@/domain/services/resource';
import GeneralTabService from '@/domain/services/installation/supervision/sensors/generalAndParameters';
import { ModuleCategorys } from 'sigma-mappings';
import EngineeringModulesHeader from '@/components/header/EngineeringModulesHeader';
import { mapGetters } from 'vuex';
import DigitalNetworkModal from '@/app/installation/supervision/DigitalNetworkModal';
import headerEvents from '@/domain/events/engineeringModulesHeader';

export default {
  name: 'Sensor',
  components: {
    ModuleHeader,
    EngineeringModulesHeader,
    DigitalNetworkModal,
  },
  props: {
    asset: {
      type: Object,
      default: () => ({}),
    },
    sensor: {
      type: Object,
    },
  },
  data: () => ({
    activeTab: null,
    sensorInformation: null,
    selectedInformation: null,
    loading: false,
    loadingSensorInformation: true,
    showModal: false,
    notifications: [],
  }),
  computed: {
    ...mapGetters(['installation']),
    currentInstallation() {
      return this.installation || {};
    },
    assetId() {
      const { assetId } = this.$route.params;
      return assetId;
    },
    sensorId() {
      const { sensorId } = this.$route.params;
      return sensorId;
    },
    isSensorComponentized() {
      return this.sensor?.category === ModuleCategorys.COMPONENTIZED;
    },
    tabs() {
      if (!this.sensor) return [];
      return this.sensor.features.map(feature => ({
        name: feature,
        component: featureTabs[feature],
        title: this.$t(`sensors.tab-titles.${feature}`),
        class: `supervision-feature__${feature}`,
      }));
    },
    routeTabName() {
      const routeTabName = this.$route.query && this.$route.query.tab;
      if (
        routeTabName
        && (this.tabs || []).some(({ name }) => name === routeTabName)
      ) {
        return routeTabName;
      }

      const [activeTab] = this.tabs || [];
      return activeTab?.name || '';
    },
    showInfoIcon() {
      return this.asset.features?.length > 0;
    },
  },
  created() {
    this.getSensorInformation();
    this.initListener();
    this.getNotifications();
  },
  beforeDestroy() {
    this.removeListener();
  },
  methods: {
    isTabActive({ name }) {
      return name === this.routeTabName;
    },
    changeRouteTab({ name }) {
      if (this.isTabActive({ name })) {
        return;
      }

      this.$router.push({
        name: this.$route.name,
        params: this.$route.params,
        query: {
          tab: name,
        },
      });
    },
    getSensorInformation() {
      this.loadingSensorInformation = true;

      if (this.isSensorComponentized) {
        return GeneralTabService.getSensorInformation(this.sensorId)
          .then((data) => {
            this.sensorInformation = data;
            [this.selectedInformation] = data;
          })
          .finally(() => {
            this.loadingSensorInformation = false;
          });
      }

      this.loadingSensorInformation = false;
      return null;
    },
    initListener() {
      eventBus.on(headerEvents.wifi, this.openModal, this);
    },
    removeListener() {
      eventBus.off(headerEvents.wifi, this.openModal, this);
    },
    openModal() {
      this.showModal = true;
    },
    renderTab(tabIndex) {
      return this.activeTab === tabIndex && !this.loadingSensorInformation;
    },
    refreshSensorData({ wait }) {
      eventBus.emit(headerEvents.refresh, { wait });
    },
    onChangeSelectedInformationHandler(value) {
      this.selectedInformation = value;
      this.$nextTick(() => {
        this.refreshSensorData({
          wait: () => Promise.resolve(),
        });
      });
    },
    async getNotifications() {
      ResourceService.getNotificationsByResourceId(this.sensorId).then((data) => {
        this.notifications = data;
      });
    },
  },
  watch: {
    $route() {
      this.getSensorInformation();
      this.getNotifications();
    },
  },
};
</script>
<style scoped lang="sass">
.sensor-data
  padding: 2rem

.tabs:not(.row) > .tab-content > .tab-pane.supervision-feature__configuration
  padding: 0
</style>
